import { useEffect, useRef, useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronDown, Languages, LogOut, Menu, Settings } from "lucide-react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { LOCALES, ROLES, type Locale } from "@salesboard/contracts";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useLocale, useT } from "@/i18n";
import { initials } from "@/lib/initials";
import { BADGE_TEXT, MANAGER_HEX, badgeFill } from "@/lib/manager-colors";
import { connectEvents, type ConnectionState } from "@/lib/sse";
import { ConnectionBanner, ConnectionProvider } from "./ConnectionState";
import { useLogout, useSession, useSetLocale } from "@/features/auth/useSession";
import { useTvMode } from "@/features/board/useTvMode";
import { LostReasonSweep } from "@/features/opportunities/LostReasonSweep";

type NavItem = {
  to: string;
  label: "nav.pipeline" | "nav.activity" | "nav.clients" | "nav.managers";
  headOfSalesOnly?: boolean;
};

/**
 * The bar: **Pipeline · Activity · Clients · Managers** (role-dependent).
 *
 * "Managers" rather than "Administration" (2026-08-26): the entry leads to the register of
 * people, and *administration* named a category rather than what is behind the link — the
 * application's configuration moved to the profile menu, where the reader's other
 * account-and-application choices already are.
 *
 * **Analytics has no entry, deliberately (M54, 2026-08-25.)** It is not missing — it is *in*
 * the pipeline: the summary strip above the board and the Detailed view that expands from it.
 * An entry pointing at a screen that no longer exists would be worse than none, which is the
 * same reasoning that kept Analytics out of this list until the metrics existed at all, and
 * the same reasoning the book applies to role-gated entries.
 *
 * **Activity is not role-gated**: both roles read the feed, and the server's `where` clause
 * decides which scopes each of them sees inside it. See `specs/activity-feed.md`.
 */
const NAV: NavItem[] = [
  { to: "/", label: "nav.pipeline" },
  { to: "/activity", label: "nav.activity" },
  { to: "/clients", label: "nav.clients" },
  { to: "/admin/users", label: "nav.managers", headOfSalesOnly: true },
];

export function AppLayout(): React.JSX.Element {
  const t = useT();
  const session = useSession();
  const logout = useLogout();
  const queryClient = useQueryClient();
  const user = session.data;
  // Presentation mode: the board fills the wall, so the application's own chrome goes. The
  // connection banner deliberately does not — a stale TV must say so.
  const { tv } = useTvMode();

  // One stream for the whole session, opened once the user is known so their own writes
  // can be told apart from everyone else's. Its state is published to the tree because the
  // board must refuse to move cards while it cannot see other people's moves.
  const [connection, setConnection] = useState<ConnectionState>("connecting");
  useEffect(() => {
    if (!user) return;
    return connectEvents(queryClient, { currentUserId: () => user.id, onStateChange: setConnection });
  }, [user, queryClient]);

  // Whether the page has scrolled away from the very top — the nav's cue to fade in its
  // own opaque backdrop rather than let content slide behind a transparent bar. A 1px
  // sentinel watched by an IntersectionObserver, not a `scroll` listener: it only ever
  // fires the two times that matter (crossing 0px each way), never once per pixel, so
  // there is nothing here for a scroll handler to thrash on. The fade itself is a CSS
  // opacity transition on the nav's backdrop below, not anything driven from here.
  const [scrolled, setScrolled] = useState(false);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(([entry]) => entry && setScrolled(!entry.isIntersecting), {
      threshold: 0,
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Where a capability is absent, its entry point is absent — a Sales Manager sees no
  // Managers item rather than one that refuses them. Presentation only: the API refuses
  // regardless of what was rendered.
  const items = NAV.filter((item) => !item.headOfSalesOnly || user?.role === ROLES.headOfSales);

  return (
    <ConnectionProvider value={connection}>
      {/* The brand's colour wash sits behind the whole shell (`brand-bloom-page`, the
          same page-scale wash the sign-in screens use) rather than only behind the nav —
          `relative` gives the top sentinel below a box to anchor to. Every data surface
          (cards, tables, modals) keeps its own opaque background on top of it, so the wash
          only ever shows through the gaps between them. */}
      <div className="brand-bloom-page relative min-h-dvh">
        {/* Sits at the very top of the document, not the header: it has to detect the
            page leaving scroll-top even while the header itself is `!tv`-hidden. */}
        <div ref={topSentinelRef} aria-hidden className="absolute top-0 h-px w-full" />

        {/* Asks for any lost reason nobody gave — see `LostReasonSweep`. Mounted in the shell
            rather than on a screen: the question follows the reader wherever they arrive. */}
        <LostReasonSweep />

        {!tv && (
          <header className="sticky top-0 z-40">
            {/*
             * z-index ladder, top to bottom — touch any of these together, not alone:
             *   50+  Radix overlays (dialog / popover / tooltip) — must cover the nav
             *   40   this header
             *   30   board corner (BoardGrid.tsx)
             *   20   board quarter headers (BoardGrid.tsx)
             *   10   board stage-row headers (BoardGrid.tsx)
             */}
            {/* The nav's own backdrop, separate from its content so only *this* layer's
                opacity animates: transparent at scroll-top (the page wash above shows
                through), fading to `brand-bloom-bar`'s opaque wash once `scrolled` is true,
                so text sliding under a see-through bar never happens. `-z-10` keeps it
                behind the nav content below rather than painted over it. */}
            <div
              aria-hidden
              className={cn(
                // Nearly opaque with a stronger blur, so what passes underneath is a *hint*
                // and not a second column of text competing with the nav's own (2026-08-26:
                // 90% let too much through). Still not a solid fill — the trace of movement
                // under the bar is what makes it read as floating over the page rather than
                // clipping it.
                "brand-bloom-bar pointer-events-none absolute inset-0 -z-10 backdrop-blur-md transition-opacity duration-300",
                scrolled ? "opacity-[0.97]" : "opacity-0",
              )}
            />
            <div className="shell-width relative flex h-16 items-center gap-6 px-4 sm:px-6">
              {/* Logo, then the application name to its right, set on two lines so its
              block matches the square logo's height rather than floating beside it. */}
              <Link
                to="/"
                aria-label={t("app.homeLink")}
                className="focus-visible:ring-ring flex items-center gap-3 rounded-md focus-visible:ring-2 focus-visible:outline-none"
              >
                <img
                  src="/infa-partner-logo.png"
                  alt={t("app.logoAlt")}
                  width={44}
                  height={44}
                  className="h-11 w-11 shrink-0 rounded-sm object-contain"
                />
                {/* The wordmark is gone (2026-08-25): the logo is the identity, the tab
                    title says the product's name, and the nav items — now the largest text in
                    the bar — are what a reader actually navigates by. `app.homeLink` keeps the
                    link named for a screen reader. */}
              </Link>

              {/* Below `lg` the same items live in a menu. One `items` array feeds both, so a
                  role losing an entry loses it in both places — a hamburger with its own copy of
                  the list is how a nav starts disagreeing with itself about permissions. */}
              <DropdownMenu.Root>
                <DropdownMenu.Trigger
                  aria-label={t("nav.main")}
                  className="hover:bg-accent focus-visible:ring-ring rounded-md p-2 focus-visible:ring-2 focus-visible:outline-none lg:hidden"
                >
                  <Menu className="h-5 w-5" aria-hidden />
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content
                    align="start"
                    sideOffset={6}
                    className="bg-popover text-popover-foreground z-50 min-w-44 rounded-md border p-1 shadow-md"
                  >
                    {items.map((item) => (
                      <DropdownMenu.Item key={item.to} asChild>
                        <NavLink
                          to={item.to}
                          className={({ isActive }) =>
                            cn(
                              "hover:bg-accent data-[highlighted]:bg-accent flex cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm outline-none",
                              isActive && "text-primary font-medium",
                            )
                          }
                        >
                          {t(item.label)}
                        </NavLink>
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>

              {/* Shallow by design: a compact bar, not an application hierarchy. Hidden below
                  `lg`, where the four items and the profile chip were overlapping — the
                  hamburger below carries the same list at those widths. */}
              <nav aria-label={t("nav.main")} className="hidden min-w-0 items-center gap-1 lg:flex">
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        // A notch larger than body text: with the page headlines gone, these
                        // are the labels that say where you are.
                        "focus-visible:ring-ring rounded-md px-3 py-1.5 text-[0.9375rem] transition-colors focus-visible:ring-2 focus-visible:outline-none",
                        isActive
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-muted-foreground hover:bg-accent hover:text-foreground",
                      )
                    }
                  >
                    {t(item.label)}
                  </NavLink>
                ))}
              </nav>

              {user && (
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger className="hover:bg-accent focus-visible:ring-ring ml-auto flex items-center gap-2 rounded-md px-2 py-1.5 text-sm focus-visible:ring-2 focus-visible:outline-none">
                    {/* The reader's own manager colour, so the circle in the bar is the same
                        circle their cards carry — the palette's tokens and the same
                        contrast-picked text colour as `OwnerBadge`, not a second scheme.
                        Unassigned keeps the brand fill it always had. */}
                    <span
                      className={cn(
                        "flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium",
                        user.color ? "" : "bg-primary text-primary-foreground",
                      )}
                      style={
                        user.color
                          ? {
                              backgroundColor: badgeFill(MANAGER_HEX[user.color]),
                              color: BADGE_TEXT,
                            }
                          : undefined
                      }
                    >
                      {initials(user.name)}
                    </span>
                    <span className="hidden sm:inline">{user.name}</span>
                    <ChevronDown className="h-4 w-4 opacity-60" aria-hidden />
                  </DropdownMenu.Trigger>

                  <DropdownMenu.Portal>
                    <DropdownMenu.Content
                      align="end"
                      sideOffset={6}
                      // z-50: a Radix popover, which the z-index ladder above requires to
                      // clear the header (z-40) regardless of scroll position.
                      className="bg-popover text-popover-foreground z-50 min-w-52 rounded-md border p-1 shadow-md"
                    >
                      <div className="px-2 py-1.5">
                        <p className="text-sm font-medium">{user.name}</p>
                        <p className="text-muted-foreground truncate text-xs">{user.email}</p>
                        <p className="text-muted-foreground mt-0.5 text-xs">{t(`role.${user.role}`)}</p>
                      </div>

                      <DropdownMenu.Separator className="bg-border my-1 h-px" />
                      <LanguageMenu />

                      {/*
                        Application settings, under the language (2026-08-26).
                        It was a button on the Users screen, which put the shape of everyone's
                        board behind a link to a list of people. Here it sits with the reader's
                        other standing choices — language, then settings, then sign out — and
                        only for the role that may change them. The API refuses the rest
                        regardless of what was rendered.
                      */}
                      {user.role === ROLES.headOfSales && (
                        <DropdownMenu.Item asChild>
                          <Link
                            to="/admin/settings"
                            className="hover:bg-accent data-[highlighted]:bg-accent flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none"
                          >
                            <Settings className="h-4 w-4" aria-hidden />
                            {t("settings.title")}
                          </Link>
                        </DropdownMenu.Item>
                      )}

                      <DropdownMenu.Separator className="bg-border my-1 h-px" />
                      <DropdownMenu.Item
                        onSelect={() => logout.mutate()}
                        className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-accent"
                      >
                        <LogOut className="h-4 w-4" aria-hidden />
                        {t("nav.signOut")}
                      </DropdownMenu.Item>
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              )}
            </div>
          </header>
        )}

        <ConnectionBanner state={connection} />

        {/* Bottom padding leaves room for the floating pagination pill; presented, the
            board gets the viewport instead of the reading width. */}
        {/* Width is the shell's, not a density's (2026-08-31). Condensed briefly widened the page
            on its own; the developer's point was that the margin was wasted in *every* state, so
            `.shell-width` is now what every screen and the nav above share. Presentation mode
            still goes edge to edge — a wall display has no reader to protect from a long line. */}
        <main className={tv ? "px-4 pt-4 pb-6" : "shell-width px-4 pt-6 pb-24 sm:px-6"}>
          <Outlet />
        </main>
      </div>
    </ConnectionProvider>
  );
}

/**
 * The language switcher.
 *
 * In the profile menu because the language is a property of the person, not of the screen — the
 * same place their name, their email and their role are, and the same place they sign out from.
 * It is a **radio group**, not two items or a toggle: with more than two languages a toggle
 * stops working, and a radio group already says which one is current without a tick drawn by
 * hand.
 *
 * The choice applies immediately and is persisted on the user row, so it follows them to the
 * next device. Nothing else needs to happen: `LocaleProvider` re-resolves from the session the
 * mutation just replaced, and the whole tree re-renders in the new language.
 *
 * `LOCALES` drives the list rather than a hand-written pair, so a third language appears here
 * as soon as the contract admits one.
 */
function LanguageMenu(): React.JSX.Element {
  const t = useT();
  const locale = useLocale();
  const setLocale = useSetLocale();

  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger className="hover:bg-accent data-[highlighted]:bg-accent flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none">
        <Languages className="h-4 w-4" aria-hidden />
        {t("nav.language")}
        <span className="text-muted-foreground ml-auto text-xs">{t(`locale.${locale}`)}</span>
      </DropdownMenu.SubTrigger>

      <DropdownMenu.Portal>
        <DropdownMenu.SubContent
          sideOffset={4}
          // z-50, matching the profile menu it nests inside — see the z-index ladder in
          // AppLayout's header.
          className="bg-popover text-popover-foreground z-50 min-w-40 rounded-md border p-1 shadow-md"
        >
          <DropdownMenu.RadioGroup
            value={locale}
            onValueChange={(next) => {
              // Choosing what is already chosen is not a change, and this write is cheap but
              // not free — it is a request and a session replacement.
              if (next === locale) return;
              setLocale.mutate(next as Locale);
            }}
          >
            {LOCALES.map((option) => (
              <DropdownMenu.RadioItem
                key={option}
                value={option}
                className="hover:bg-accent data-[highlighted]:bg-accent data-[state=checked]:font-medium flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none"
              >
                {/* Each language is named **in itself** — a Czech reader looking for Czech
                    should not have to recognise the word "Czech". */}
                {t(`locale.${option}`)}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
}

